import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Box, Typography, CircularProgress, Checkbox } from "@mui/material";
import FolderIcon from "@mui/icons-material/Folder";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { renderTruncatedText } from "../../core/Constant";

const isNodeMatchingSearch = (node, query) => {
  if (!query) return true;
  const q = query.trim().toLowerCase();
  const nameMatches = node?.name?.toLowerCase().includes(q);
  if (nameMatches) return true;

  const children = Array.isArray(node?.children)
    ? node.children
    : Object.values(node?.children || {});

  return children.some((child) => isNodeMatchingSearch(child, query));
};

const FolderTreeNode = ({
  node,
  selectedFolderIds = [],
  onFolderToggle,
  searchQuery,
  disabled = false,
}) => {
  const children = Object.values(node?.children || {});

  const filteredChildren = useMemo(() => {
    if (!searchQuery?.trim()) return children;
    return children.filter((child) => isNodeMatchingSearch(child, searchQuery));
  }, [children, searchQuery]);

  const hasChildren = filteredChildren.length > 0;

  const formattedSelectedIds = useMemo(
    () => selectedFolderIds.map(String),
    [selectedFolderIds]
  );

  // Check if current node is selected
  const isSelected = formattedSelectedIds.includes(String(node?.id));

  const hasSelectedChild = useCallback((currentNode) => {
    if (!currentNode) return false;

    const childNodes = Array.isArray(currentNode.children)
      ? currentNode.children
      : Object.values(currentNode.children || {});

    return childNodes.some((child) => {
    const childIdStr = String(child.id);
    return (
      formattedSelectedIds.includes(childIdStr) ||
        hasSelectedChild(child)
    );
    });
  }, [formattedSelectedIds]);

  const isAnyChildSelected = useMemo(
    () => hasSelectedChild(node),
    [node, hasSelectedChild]
  );

  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (searchQuery?.trim() || isAnyChildSelected || isSelected) {
      setOpen(true);
    }
  }, [searchQuery, isAnyChildSelected, isSelected]);

  if (!isNodeMatchingSearch(node, searchQuery)) {
    return null;
  }

  return (
    <Box sx={{ position: "relative" }}>
      {/* Node Row Layout */}
      <Box sx={{
          display: "flex",
          alignItems: "center",
          py: 0.5,
          px: 1,
          borderRadius: 1,
          // Give visual highlighting for selected folders
          backgroundColor: isSelected
            ? "#F9F5FF"
            : isAnyChildSelected
            ? "#FCFAFF"
            : "transparent",
        }}
      >
        <Box
          sx={{
            width: 24,
            height: 24,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: hasChildren ? "pointer" : "default",
            color: "#98A2B3",
          }}
          onClick={() => hasChildren && setOpen((prev) => !prev)}
        >
          {hasChildren &&
            (open ? (
              <ExpandMoreIcon fontSize="small" />
            ) : (
              <ChevronRightIcon fontSize="small" />
            ))}
        </Box>

        {/* Folder Icon */}
        <FolderIcon sx={{ color: isSelected ? "#7F56D9" : "#EAAA08", mr: 1, fontSize: "20px"}}/>

        {/* Checkbox Selector */}
        <Checkbox
          size="small"
          disabled={disabled}
          checked={isSelected}
          onChange={() => !disabled && onFolderToggle(node.id)}
          sx={{ p: 0.5, mr: 1 }}
        />

        {/* Text Label wrapper (expanding tree on click) */}
        <Typography
          onClick={() => hasChildren && setOpen((prev) => !prev)}
          sx={{
            fontSize: 14,
            fontWeight: isSelected ? 700 : isAnyChildSelected ? 600 : 500,
            color: isSelected ? "#6941C6" : isAnyChildSelected ? "#101828" : "#344054",
            cursor: hasChildren ? "pointer" : "default",
            userSelect: "none",
          }}
        >
          {/* {node.name} */}
          {renderTruncatedText(node?.name, 30)}
        </Typography>
      </Box>

      {/* Children Tree Loop Segment */}
      {open && hasChildren && (
        <Box sx={{ borderLeft: "1px dashed #EAECF0", ml: "11px", pl: 2 }}>
          {filteredChildren.map((child) => (
            <FolderTreeNode
              key={child.id}
              node={child}
              selectedFolderIds={selectedFolderIds}
              onFolderToggle={onFolderToggle}
              searchQuery={searchQuery}
              disabled={disabled}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

// Main Export Wrapper
const FolderTreeSelection = ({
  foldersData,
  loading,
  selectedFolderIds = [],
  onFolderToggle,
  searchQuery = "",
  containerSx = {},
  disabled = false,
}) => {
  // Normalization layer utility handling dynamic wrapper layers securely
  const getNormalizedFolderList = () => {
    if (!foldersData) return [];
    let targetData = foldersData.quiz_directories ? foldersData.quiz_directories : foldersData;
    if (Array.isArray(targetData)) targetData = targetData[0] || {};
    if (targetData && targetData.quiz_directories) targetData = targetData.quiz_directories;
    return Object.values(targetData || {});
  };

  const folderList = getNormalizedFolderList();

  const filteredFolderList = useMemo(() => {
    if (!searchQuery?.trim()) return folderList;
    return folderList.filter((folder) =>
      isNodeMatchingSearch(folder, searchQuery)
    );
  }, [folderList, searchQuery]);

  return (
    <Box
      sx={{
        border: "1px solid #E4E7EC",
        borderRadius: 2,
        p: 2,
        height: "auto",
        maxHeight: "none",
        overflow: "visible",
        ...containerSx,
      }}
    >
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
          <CircularProgress />
        </Box>
      ) : filteredFolderList.length === 0 ? (
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          No folders found matching "{searchQuery}".
        </Typography>
      ) : (
        filteredFolderList.map((folder) => (
          <FolderTreeNode
            key={folder.id}
            node={folder}
            selectedFolderIds={selectedFolderIds}
            onFolderToggle={onFolderToggle}
            searchQuery={searchQuery}
            disabled={disabled}
          />
        ))
      )}
    </Box>
  );
};

export default FolderTreeSelection;